import React, { useState, useCallback, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import Sidebar from '../components/Sidebar';
import FocusedPanel from '../components/FocusedPanel';
import SearchModal from '../components/SearchModal';
import SettingsModal from '../components/SettingsModal';
import AgendaPanel from '../components/AgendaPanel';
import UndoToastStack from '../components/UndoToast';
import MobileFormatBar from '../components/MobileFormatBar';
import SaveIndicator from '../components/SaveIndicator';
import Wordmark from '../components/Wordmark';
import { IconPencil, IconColor, IconTrash, IconPlus } from '../components/MenuKit';
import { LIST_COLORS } from '../lib/listColors';
import { useSettings } from '../hooks/useSettings';
import { isRemindersEnabled } from '../hooks/useUserPrefs';
import { useOverdueHiddenUntil } from '../lib/overdueHide';

// Mobile layout for lists.
//
// Two-state master/detail:
//   navBig=true   — sidebar occupies full viewport width, no tile
//   navBig=false  — sidebar compacts to an 80px strip on the left,
//                   the selected tile fills the rest of the viewport
//
// Interactions:
//   tap a different row              → select that tile + navBig=false
//   tap the *already-active* row     → toggle navBig (expand ↔ collapse)
//   tap the dedicated expand button  → toggle navBig
//   tap X on tile                    → clear active + navBig=true
//
// The desktop canvas, drag-and-resize, auto-arrange, sidebar-toggle and
// grid overlay are intentionally omitted — they don't make sense on a
// phone-sized screen. Rearranging is still possible via sidebar drag.
export default function MobileBoard({
  user,
  board,
  boards,
  lists,
  // Walter 2026-06-16: true while the lists snapshot hasn't arrived — gates
  // the empty-board card so it doesn't flash on every reload.
  listsLoading = false,
  // Walter 2026-08-12: edits saved on this device but not yet in the cloud —
  // drives the `unsynced` caption. Mobile matters most here; it's the device
  // most likely to have a save fail and go unnoticed.
  queuedCount = 0,
  userPrefs,
  updateUserPrefs,
  createReminder,
  reminders,
  updateReminder,
  updateReminderInAgenda,
  updateReminderNotes,
  deleteReminder,
  deactivateReminder,
  fullyDeleteReminder,
  pushSub,
  pushToast,
  onSignOut,
  onLinkGoogle,
  onLinkMagicLink,
  onSwitchBoard,
  onNewBoard,
  onRenameBoard,
  onDeleteBoard,
  // Walter 2026-06-29: the sidebar board ⋯ + list-colour picker (nav cleanup)
  // need these on mobile too — the mobile Sidebar was missing them, so those
  // features were dead on phones.
  onUpdateBoard,
  onSetListColor,
  onReorderBoards,
  // List-level callbacks (same ones BoardPage gives the desktop sidebar)
  onAddList,
  // Walter 2026-06-15 (s1): seeded reminder-list creator for the
  // onboarding "Set up a reminder" step (mobile was missing it).
  onTryReminderList,
  onUpdate,
  onArchive,
  onUnarchive,
  onDelete,
  onMoveList,
  onReorder,
  // Toggle a list's designated-reminder-list status from its ⋯ menu.
  onToggleReminderList,
  // Open Share modal scoped to a specific list (Phase 2 sharing).
  onShareList,
  // Always-wired peek of who has access to a list (alpha-independent).
  // Backs the inline ShareBadge on each list surface.
  onPeekShare,
  // Walter 2026-05-11: passed through to FocusedPanel so the
  // textarea's 🔁-click handler can open the structured edit popover.
  onOpenReminderEditor,
  // Owner uid for the current board — for shared boards where the
  // current user is a guest editor, this is the inviter's uid (not
  // user.uid). Plumbed in from BoardPage which gets it from BoardRoute
  // via useResolvedBoard.
  boardOwnerUid,
  // Boards/lists shared with the current user — see useSharedWithMe.
  // Surfaced as a section in the sidebar (when expanded) so mobile
  // users can navigate to shared content without bookmarks.
  sharedWithMe,
  // Walter 2026-05-11: per-user shared-list placements. Used by the
  // Sidebar's SharedWithMeSection filter (placed lists move out of
  // that section into the target board's row list).
  sharedListPlacements,
  // True when the current user is a guest editor/viewer on the
  // active board. Drives "isSharedWithMe" on the panel so the
  // "place in board…" item shows.
  isGuestOnBoard = false,
  // Open the "place in board…" picker for a shared list.
  onPlaceList,
  // Save-status wiring (shared between FocusedPanel + header pill)
  saveStatus,
  onSaveStatus,
  // Walter 2026-05-15: registry callbacks for the new SaveIndicator's
  // tap-to-force-sync gesture. registerPendingFlush is forwarded to
  // each child FocusedPanel so they can register their flushSave;
  // onForceSync is bound to the floppy tap.
  registerPendingFlush,
  onForceSync,
}) {
  const [navBig, setNavBig] = useState(true);
  // Phase 3 (mobile expandable peek): tri-state nav width.
  //   navBig=true                              → full-width sidebar, tile hidden (entry state, "browse boards")
  //   navBig=false, peekExpanded=false         → 80px peek strip + tile (default work mode, 1-tap list switch)
  //   navBig=false, peekExpanded=true          → 240px peek-expanded + tile (BOARDS / SHARED visible without giving up the tile)
  // The 240px state is new — Walter's "expandable peek" so users
  // can switch boards / hop into shared content without losing
  // their place in the current tile. We auto-collapse back to 80px
  // on board switch so the tile area reclaims width immediately.
  const [peekExpanded, setPeekExpanded] = useState(false);
  const [activeListId, setActiveListId] = useState(null);
  const [showBoardMenu, setShowBoardMenu] = useState(false);
  // Walter 2026-06-29: parity with the desktop board ⋯ — colour sub-view +
  // inline rename (replacing the old window.prompt).
  const [boardMenuMode, setBoardMenuMode] = useState('main'); // 'main' | 'color'
  const [renamingBoard, setRenamingBoard] = useState(false);
  const [boardNameDraft, setBoardNameDraft] = useState('');
  const [showSearch, setShowSearch] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  // Walter 2026-06-01: when Settings is opened from the onboarding
  // "sign in" CTA, open it with the account section expanded (sign-in
  // options visible immediately) rather than collapsed.
  const [settingsExpandAccount, setSettingsExpandAccount] = useState(false);
  // Walter 2026-06-13 (s3): the App-level GuestUpgradeHint banner's "sign in"
  // dispatches a window event; on mobile, open Settings → account section (the
  // same surface the in-app sign-in prompts use here). Desktop is handled by
  // BoardPage's own listener.
  useEffect(() => {
    const open = () => { setSettingsExpandAccount(true); setShowSettings(true); };
    window.addEventListener('listicity:request-account', open);
    return () => window.removeEventListener('listicity:request-account', open);
  }, []);
  const [showAgenda, setShowAgenda] = useState(false);
  const overdueHiddenForBadge = useOverdueHiddenUntil();
  const [jumpTarget, setJumpTarget] = useState(null);
  const [viewportW, setViewportW] = useState(() =>
    typeof window !== 'undefined' ? window.innerWidth : 360
  );
  const boardMenuRef = useRef(null);
  // Position of the board-menu dropdown, computed from the trigger's
  // bounding rect when we open the menu. We render the dropdown with
  // position: fixed (instead of absolute) so it escapes the header's
  // overflow-x: hidden clipping context. The header sets overflow-x
  // hidden as a horizontal-overflow defense for iOS Safari — and CSS
  // coerces overflow-y to `auto` whenever overflow-x is non-visible,
  // which clips absolutely-positioned descendants to the 44px header
  // height. Walter 2026-05-15 caught this: the ⋯ menu was opening but
  // only the top 2px (the rounded top border) was peeking below the
  // header line. position: fixed bypasses ancestor overflow entirely.
  const [boardMenuPos, setBoardMenuPos] = useState(null);
  // Holds the id of a just-created list until its Firestore snapshot arrives
  // locally. Needed because addList's promise resolves (returning the new id)
  // *before* the onSnapshot listener populates `lists`, and without this
  // marker the cleanup effect below would see activeListId pointing at a
  // list not yet in `lists` and clear it — making the tile snap back to the
  // big nav instead of opening the new list. Cleared as soon as the list
  // actually appears in `lists`.
  const pendingNewListIdRef = useRef(null);
  const {
    theme, setTheme,
    palette, setPalette,
    fontSize, setFontSize,
    lineHeight, setLineHeight,
  } = useSettings();

  // Outside-tap dismiss for the board-switcher dropdown. Uses mousedown so the
  // Walter 2026-05-10: tab title reflects active board on mobile too —
  // same rationale as desktop BoardPage (lets users identify the right
  // tab when several boards are open in parallel tabs).
  useEffect(() => {
    const name = (board?.name || '').trim();
    // Walter 2026-05-15: in standalone PWA mode the title bar would
    // otherwise read "listicity - Foo · listicity" (the window
    // manager prepends manifest.name). Drop the suffix when
    // standalone; preserve it in browser tabs where it's the only
    // brand context.
    const standalone = (typeof window !== 'undefined') && (
      window.navigator.standalone === true
      || (window.matchMedia && window.matchMedia('(display-mode: standalone)').matches)
    );
    if (standalone) {
      document.title = name || 'listicity';
    } else {
      document.title = name ? `${name} · listicity` : 'listicity';
    }
    return () => { document.title = 'listicity'; };
  }, [board?.name]);

  // dismiss runs before any click inside the menu fires, but also listens for
  // touchstart so it works on phones where clicks are synthesised from taps.
  useEffect(() => {
    if (!showBoardMenu) { setBoardMenuMode('main'); setRenamingBoard(false); return; }
    const onDown = (e) => {
      if (boardMenuRef.current && !boardMenuRef.current.contains(e.target)) {
        setShowBoardMenu(false);
      }
    };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('touchstart', onDown);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('touchstart', onDown);
    };
  }, [showBoardMenu]);

  // Sidebar takes a pixel `width` when !compact — on mobile that's the whole
  // viewport, so keep it in sync with resize / rotation.
  useEffect(() => {
    const onResize = () => setViewportW(window.innerWidth);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  const activeList = activeListId ? lists.find(l => l.id === activeListId) : null;

  // If the active list gets deleted elsewhere (e.g. desktop tab), drop it.
  // Skips clearing when the activeListId matches pendingNewListIdRef —
  // that's a list we just created locally; its snapshot is about to arrive
  // from Firestore and we don't want to snap back to the nav in the gap.
  useEffect(() => {
    if (!activeListId) return;
    const found = lists.some(l => l.id === activeListId);
    if (found) {
      // Clear the pending marker as soon as the list materializes so normal
      // delete-elsewhere cleanup resumes.
      if (pendingNewListIdRef.current === activeListId) {
        pendingNewListIdRef.current = null;
      }
      return;
    }
    if (pendingNewListIdRef.current === activeListId) return;
    setActiveListId(null);
    setNavBig(true);
  }, [lists, activeListId]);

  // Escape key dismisses modals. Tile exit is handled inside FocusedPanel.
  useEffect(() => {
    const h = (e) => {
      if (e.key === 'Escape') {
        setShowSearch(false);
        setShowSettings(false);
        setShowBoardMenu(false);
      }
    };
    window.addEventListener('keydown', h);
    return () => window.removeEventListener('keydown', h);
  }, []);

  // Row click — tapping the already-active row toggles the nav (so the user
  // can re-expand the big nav by tapping the tile they're in, and re-collapse
  // back to the tile with a second tap). Tapping a *different* row selects it
  // and collapses to compact so the newly selected tile is visible.
  // Closed lists are reopened on click (mirrors desktop); archived notes open
  // for viewing without a state change.
  const handleSelectRow = useCallback((list) => {
    if (list.state === 'closed') {
      onUpdate(list.id, { state: 'open' });
    }
    if (list.id === activeListId) {
      setNavBig(v => !v);
      return;
    }
    setActiveListId(list.id);
    setNavBig(false);
    // Auto-collapse the 240px expanded peek when the user picks a
    // list — they're done browsing, the tile should reclaim the
    // width so the note has room.
    setPeekExpanded(false);
  }, [onUpdate, activeListId]);

  // Wrapper around the parent's onSwitchBoard so we can also reset
  // the mobile nav state when the user picks a board from the
  // sidebar's BOARDS section. Same auto-collapse rationale as
  // handleSelectRow — the user's done browsing once they switch.
  const handleSwitchBoard = useCallback((boardId) => {
    onSwitchBoard?.(boardId);
    setPeekExpanded(false);
    // Don't clear activeListId or change navBig — the parent will
    // re-render with the new board and the user still wants to
    // see the tile area (empty or otherwise) on the right.
  }, [onSwitchBoard]);

  // Tile close (X in FocusedPanel header, or Esc). Clear active, nav back big.
  const handleTileExit = useCallback(() => {
    setActiveListId(null);
    setNavBig(true);
  }, []);

  // Jump target plumbing for search → tile.
  const handleJump = useCallback((list, charOffset) => {
    if (list.boardId !== board?.id) {
      onSwitchBoard(list.boardId);
      setTimeout(() => {
        setActiveListId(list.id);
        setNavBig(false);
        setJumpTarget({ listId: list.id, charOffset: charOffset ?? 0 });
      }, 300);
      return;
    }
    setActiveListId(list.id);
    setNavBig(false);
    setJumpTarget({ listId: list.id, charOffset: charOffset ?? 0 });
  }, [board, onSwitchBoard]);

  const handleAddList = useCallback(async () => {
    const newList = await onAddList?.();
    // If the parent returns the created list, open it straight away.
    if (newList?.id) {
      // Marker prevents the cleanup effect from clearing activeListId in
      // the window between "promise resolved" and "Firestore snapshot
      // delivered the new doc to our lists prop".
      pendingNewListIdRef.current = newList.id;
      setActiveListId(newList.id);
      setNavBig(false);
    }
  }, [onAddList]);

  if (!board) {
    return (
      <div style={{
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        height: '100vh', color: 'var(--text-dim)', fontFamily: 'var(--mono)',
      }}>
        No board selected.
      </div>
    );
  }

  const showTile = !navBig && activeList;

  return (
    <div style={{ height: '100dvh', display: 'flex', flexDirection: 'column', overflow: 'hidden', maxWidth: '100vw', width: '100%' }}>
      {/* ── Header ── */}
      <div style={{
        height: 'var(--header-h)', background: 'var(--surface)',
        borderBottom: '1px solid var(--border)',
        display: 'flex', alignItems: 'center',
        padding: '0 8px', gap: '4px',
        flexShrink: 0, zIndex: 500, position: 'relative',
        // Walter 2026-05-09: maxWidth + overflow-x belt-and-braces
        // so the header can't push wider than the viewport even if
        // a child somehow exceeds its share. Was showing "ticity"
        // instead of "listicity" on iPhone after keyboard scroll.
        maxWidth: '100vw',
        overflowX: 'hidden',
        minWidth: 0,
      }}>
        {/* Walter 2026-05-09: wordmark links to /landing same as desktop. */}
        <Link to="/landing" style={{ textDecoration: 'none', display: 'inline-flex' }}>
          <Wordmark fontSize={14} showSuffix={false} />
        </Link>
        <div style={{ width: 1, height: 20, background: 'var(--border)', margin: '0 2px', flexShrink: 0 }} />

        {/* Walter 2026-05-09: replaced the board-name chip (max-width
            110px) with a small ⋯ icon button. The sidebar always shows
            the active board, so the chip was redundant identity; it
            also bled into the + button on narrow phones. Keeping just
            board-management actions (new / rename / delete) here —
            board switching lives in the sidebar. */}
        <div ref={boardMenuRef} style={{ position: 'relative', flexShrink: 0 }}>
          <IconBtn
            onClick={(e) => {
              // Capture the trigger's screen position before toggling
              // so the dropdown — rendered position: fixed to escape
              // the header's overflow clipping — knows where to land.
              if (!showBoardMenu) {
                const trigger = e.currentTarget;
                const rect = trigger.getBoundingClientRect();
                setBoardMenuPos({ top: rect.bottom + 4, left: rect.left });
              }
              setShowBoardMenu(v => !v);
            }}
            title="board options"
          >
            <svg width="14" height="14" viewBox="0 0 14 14" fill="currentColor" aria-hidden="true">
              <circle cx="3" cy="7" r="1.3" />
              <circle cx="7" cy="7" r="1.3" />
              <circle cx="11" cy="7" r="1.3" />
            </svg>
          </IconBtn>

          {showBoardMenu && boardMenuPos && (
            <div style={{
              position: 'fixed',
              top: boardMenuPos.top,
              left: boardMenuPos.left,
              background: 'var(--surface)', border: '1px solid var(--border-bright)',
              borderRadius: 'var(--radius)', minWidth: '200px',
              boxShadow: 'var(--shadow-popover)',
              zIndex: 1000, overflow: 'hidden',
            }}>
              {boardMenuMode === 'color' ? (
                <>
                  <MenuItem onClick={() => setBoardMenuMode('main')} color="var(--text-dimmer)">← back</MenuItem>
                  <MenuItem
                    icon={<span style={{ display: 'inline-block', width: 10, height: 10, borderRadius: '50%', border: '1px solid var(--border-bright)' }} />}
                    onClick={() => { onUpdateBoard?.(board.id, { color: null }); setShowBoardMenu(false); }}
                  >none</MenuItem>
                  {LIST_COLORS.map(c => (
                    <MenuItem
                      key={c.id}
                      icon={<span style={{ display: 'inline-block', width: 10, height: 10, borderRadius: '50%', background: c.value }} />}
                      onClick={() => { onUpdateBoard?.(board.id, { color: c.id }); setShowBoardMenu(false); }}
                    >{c.label}</MenuItem>
                  ))}
                </>
              ) : (
                <>
                  {/* Board name — quiet context, and now inline-editable (rename)
                      instead of a window.prompt. Switching happens in the sidebar. */}
                  {renamingBoard ? (
                    <div style={{ padding: '6px 10px' }}>
                      <input
                        autoFocus
                        value={boardNameDraft}
                        onChange={e => setBoardNameDraft(e.target.value)}
                        onKeyDown={e => {
                          if (e.key === 'Enter') { const n = boardNameDraft.trim(); if (n && n !== board.name) onRenameBoard(board.id, n); setShowBoardMenu(false); }
                          else if (e.key === 'Escape') { setRenamingBoard(false); }
                        }}
                        onBlur={() => { const n = boardNameDraft.trim(); if (n && n !== board.name) onRenameBoard(board.id, n); setRenamingBoard(false); }}
                        style={{ width: '100%', boxSizing: 'border-box', background: 'var(--surface)', border: '1px solid var(--accent)', color: 'var(--text)', fontFamily: 'var(--mono)', fontSize: 'calc(12px * var(--ui-scale, 1))', padding: '5px 8px', borderRadius: 'var(--radius)', outline: 'none' }}
                      />
                    </div>
                  ) : (
                    <div style={{
                      padding: '8px 14px 4px', fontFamily: 'var(--mono)',
                      fontSize: 'calc(9px * var(--ui-scale, 1))',
                      color: 'var(--text-dimmer)', letterSpacing: '0.5px',
                      textTransform: 'uppercase',
                    }}>
                      board · {board.name}
                    </div>
                  )}
                  <MenuItem icon={<IconPlus />} onClick={() => { onNewBoard(); setShowBoardMenu(false); }} color="var(--accent)">
                    new board
                  </MenuItem>
                  <MenuItem icon={<IconPencil />} onClick={() => { setBoardNameDraft(board.name || ''); setRenamingBoard(true); }}>
                    rename
                  </MenuItem>
                  {onUpdateBoard && (
                    <MenuItem icon={<IconColor />} onClick={() => setBoardMenuMode('color')}>
                      colour {'→'}
                    </MenuItem>
                  )}
                  {boards.length > 1 && (
                    <MenuItem
                      icon={<IconTrash />}
                      onClick={() => { if (window.confirm(`Move board "${board.name}" and all its lists to trash?\n\nYou can restore it within 30 days from the trash menu (account popover → trash).`)) { onDeleteBoard(board.id); } setShowBoardMenu(false); }}
                      color="var(--danger)"
                    >
                      delete
                    </MenuItem>
                  )}
                </>
              )}
            </div>
          )}
        </div>

        {/* + new list (icon only) */}
        <IconBtn onClick={handleAddList} title="new list" accent>
          <svg width="12" height="12" viewBox="0 0 10 10" fill="none">
            <path d="M5 1v8M1 5h8" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
          </svg>
        </IconBtn>

        {/* search */}
        <IconBtn onClick={() => setShowSearch(true)} title="search">
          <svg width="12" height="12" viewBox="0 0 10 10" fill="none">
            <circle cx="4" cy="4" r="3" stroke="currentColor" strokeWidth="1.3"/>
            <path d="M6.5 6.5l2 2" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round"/>
          </svg>
        </IconBtn>

        <div style={{ flex: 1 }} />

        {/* Walter 2026-05-15: replaced the 16px spinner/check with the
            richer SaveIndicator (floppy + countdown + typing + syncing
            states; tappable to force-flush). Compact variant is sized
            for the 44px mobile header. See SaveIndicator.js. */}
        <SaveIndicator status={saveStatus} onForceSync={onForceSync} compact queuedCount={queuedCount} />

        {/* Bell — opens agenda. Always shown (Walter 2026-05-06
            round 6) so the affordance is discoverable; the agenda's
            empty state explains how to turn the feature on if it
            isn't. Count badge mirrors the desktop bell — counts open
            + dated reminders that are due today or overdue. */}
        {(() => {
          const now = new Date();
          const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
          const overdueHidden = overdueHiddenForBadge > Date.now();
          const dueOrOverdue = (reminders || []).filter(r => {
            if (r.status === 'done') return false;
            const d = r.parsedDate?.toDate?.() || (r.parsedDate ? new Date(r.parsedDate) : null);
            if (!d) return false;
            if (overdueHidden && d < now) return false;
            return d <= now || (d >= today && d < new Date(today.getTime() + 86400000));
          }).length;
          return (
            <div style={{ position: 'relative', flexShrink: 0 }}>
              <IconBtn onClick={() => setShowAgenda(true)} title="reminders">
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none"
                  stroke="currentColor" strokeWidth="1.8"
                  strokeLinecap="round" strokeLinejoin="round">
                  <path d="M6 8a6 6 0 0 1 12 0c0 7 3 9 3 9H3s3-2 3-9"/>
                  <path d="M10.3 21a1.94 1.94 0 0 0 3.4 0"/>
                </svg>
              </IconBtn>
              {dueOrOverdue > 0 && (
                <span style={{
                  position: 'absolute', top: 0, right: 0,
                  minWidth: 14, height: 14,
                  borderRadius: 7,
                  background: 'var(--accent)',
                  color: 'var(--bg)',
                  fontSize: 9, fontWeight: 700,
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  padding: '0 3px',
                  fontFamily: 'var(--mono)',
                  pointerEvents: 'none',
                }}>{dueOrOverdue}</span>
              )}
            </div>
          );
        })()}
        {/* settings — also hosts a collapsible "account" row at the
            top (Walter 2026-05-13: a separate account icon in the
            header cluttered the top bar; account info now lives as
            an expand-on-tap section inside settings). */}
        <IconBtn onClick={() => { setSettingsExpandAccount(false); setShowSettings(true); }} title="settings">
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none"
            stroke="currentColor" strokeWidth="1.8"
            strokeLinecap="round" strokeLinejoin="round">
            <path d="M12.22 2h-.44a2 2 0 0 0-2 2v.18a2 2 0 0 1-1 1.73l-.43.25a2 2 0 0 1-2 0l-.15-.08a2 2 0 0 0-2.73.73l-.22.38a2 2 0 0 0 .73 2.73l.15.1a2 2 0 0 1 1 1.72v.51a2 2 0 0 1-1 1.74l-.15.09a2 2 0 0 0-.73 2.73l.22.38a2 2 0 0 0 2.73.73l.15-.08a2 2 0 0 1 2 0l.43.25a2 2 0 0 1 1 1.73V20a2 2 0 0 0 2 2h.44a2 2 0 0 0 2-2v-.18a2 2 0 0 1 1-1.73l.43-.25a2 2 0 0 1 2 0l.15.08a2 2 0 0 0 2.73-.73l.22-.39a2 2 0 0 0-.73-2.73l-.15-.08a2 2 0 0 1-1-1.74v-.5a2 2 0 0 1 1-1.74l.15-.09a2 2 0 0 0 .73-2.73l-.22-.38a2 2 0 0 0-2.73-.73l-.15.08a2 2 0 0 1-2 0l-.43-.25a2 2 0 0 1-1-1.73V4a2 2 0 0 0-2-2z"/>
            <circle cx="12" cy="12" r="3"/>
          </svg>
        </IconBtn>
      </div>

      {/* ── Body: compact-or-big sidebar + optional tile ── */}
      <div style={{ flex: 1, display: 'flex', minHeight: 0 }}>
        {/* Sidebar column: dedicated expand/collapse button stacked above the
            Sidebar. Tapping a different row selects that tile + collapses;
            tapping the already-active row toggles the nav (so the tile stays
            but the nav re-expands). The toggle button offers the same expand/
            collapse action as a dedicated control. The button row hides in
            the initial big-mode empty state (no tile to collapse to). */}
        <div style={{
          display: 'flex', flexDirection: 'column', flexShrink: 0,
        }}>
          {(!navBig || activeList) && (
            <button
              onClick={() => {
                // Cycle: peek (80) → peek-expanded (240) → peek (80).
                // navBig=true (full-width) is reachable via tile-X; from
                // there a tap collapses straight to peek 80.
                if (navBig) {
                  setNavBig(false);
                  setPeekExpanded(false);
                } else if (peekExpanded) {
                  setPeekExpanded(false);
                } else {
                  setPeekExpanded(true);
                }
              }}
              title={
                navBig
                  ? 'collapse to peek'
                  : (peekExpanded ? 'collapse to peek' : 'expand to peek-expanded (browse boards)')
              }
              style={{
                display: 'flex', alignItems: 'center',
                justifyContent: navBig ? 'flex-end' : 'center',
                /* Matches FocusedPanel's 40px header height so the tile header
                   and this toggle row sit on the same horizontal baseline. */
                height: '40px',
                padding: navBig ? '0 12px' : 0,
                background: 'var(--surface)',
                border: 'none',
                borderBottom: '1px solid var(--border)',
                color: 'var(--text-dim)',
                cursor: 'pointer',
                transition: 'color 0.15s, background 0.15s',
              }}
              onMouseEnter={e => { e.currentTarget.style.color = 'var(--accent)'; e.currentTarget.style.background = 'var(--surface2)'; }}
              onMouseLeave={e => { e.currentTarget.style.color = 'var(--text-dim)'; e.currentTarget.style.background = 'var(--surface)'; }}
            >
              <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
                {(navBig || peekExpanded) ? (
                  /* collapse (chevron-left, pointing back to peek) */
                  <path d="M8.5 2L3.5 7l5 5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                ) : (
                  /* expand (chevron-right, pointing outward to widen) */
                  <path d="M5.5 2l5 5-5 5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                )}
              </svg>
            </button>
          )}

          <div style={{ flex: 1, minHeight: 0, display: 'flex' }}>
            <Sidebar
              lists={lists}
              focusedId={activeListId}
              // Walter 2026-06-01: the mobile sidebar was missing the
              // onboarding-checklist inputs the desktop one gets
              // (BoardPage). Without isAnonymous, Sidebar fell back to its
              // `false` default, so the checklist marked "Sign in" as DONE
              // even for a guest. Thread them through here too.
              isAnonymous={!!user?.isAnonymous}
              hasShared={
                (Array.isArray(boards) && boards.some(b => (b?.editors?.length || 0) + (b?.viewers?.length || 0) > 0)) ||
                (Array.isArray(lists) && lists.some(l => (l?.editors?.length || 0) + (l?.viewers?.length || 0) > 0))
              }
              onRequestSignIn={() => { setSettingsExpandAccount(true); setShowSettings(true); }}
              onOpenSettings={() => { setSettingsExpandAccount(false); setShowSettings(true); }}
              // s1: onboarding "Set up a reminder" step — creates a seeded
              // reminder list, then collapses the nav so the new list is visible.
              onTryReminderList={onTryReminderList ? () => { setPeekExpanded(false); setNavBig(false); onTryReminderList(); } : undefined}
              // Sidebar width per state, scaled by the user's chosen
              // note-font-size (XL = 16px → ~1.33× scale). Tahmid
              // 2026-05-24: at XL the unscaled 80px peek was cramped
              // and truncated to "orga…" / "MA…" — scaling keeps the
              // sidebar legible at every font size.
              //   navBig=true                  → full viewport (browse boards)
              //   peekExpanded=true            → 240px × scale peek-expanded
              //   default                      → 80px × scale peek (compact mode handles 80 hardcode)
              width={navBig ? viewportW : Math.round((peekExpanded ? 240 : 80) * ((fontSize || 12) / 12))}
              onResize={() => {}}
              onSelectRow={handleSelectRow}
              onArchive={(l) => onArchive(l.id)}
              onUnarchive={(l) => onUnarchive(l.id)}
              onDelete={(l) => onDelete(l.id)}
              onReorder={onReorder}
              // compact = peek 80px mode only. peek-expanded (240) is
              // non-compact so the BOARDS / SHARED / TRASH sections
              // render — that's the whole point of expanding.
              compact={!navBig && !peekExpanded}
              touch
              boards={boards}
              currentBoardId={board?.id}
              boardColor={board?.color}
              onRenameBoard={onRenameBoard}
              onUpdateBoard={onUpdateBoard}
              onDeleteBoard={onDeleteBoard}
              onSetListColor={onSetListColor}
              onReorderBoards={onReorderBoards}
              onMoveList={onMoveList}
              designatedReminderListIds={userPrefs?.designatedReminderListIds || []}
              onToggleReminderList={onToggleReminderList}
              remindersFeatureOn={true}
              onShareList={onShareList}
              onPeekShare={onPeekShare}
              sharedWithMe={sharedWithMe}
              sharedListPlacements={sharedListPlacements}
              // handleSwitchBoard wraps onSwitchBoard so the peek-
              // expanded state auto-collapses to 80px when a user
              // picks a board from the sidebar's BOARDS section.
              onNavigateBoard={handleSwitchBoard}
              onNavigateList={(listId) => {
                // Standalone shared lists: switch to /list/:id and
                // collapse the peek so the list page has full width.
                setPeekExpanded(false);
                window.location.href = `/list/${listId}`;
              }}
              onNewBoard={() => { setPeekExpanded(false); onNewBoard?.(); }}
              reminders={reminders}
              listIdToBoardId={null}
              onOpenTrash={() => { setPeekExpanded(false); window.location.href = '/trash'; }}
            />
          </div>
        </div>

        {/* Detail pane — only when we have an active list and nav is compact. */}
        {showTile && (
          <FocusedPanel
            list={activeList}
            onUpdate={onUpdate}
            onExit={handleTileExit}
            onSaveStatus={onSaveStatus}
            registerPendingFlush={registerPendingFlush}
            boards={boards}
            currentBoardId={board?.id}
            onMoveList={onMoveList}
            onArchive={onArchive}
            onUnarchive={onUnarchive}
            onDelete={onDelete}
            userPrefs={userPrefs}
            updateUserPrefs={updateUserPrefs}
            createReminder={createReminder}
            reminders={reminders}
            updateReminder={updateReminder}
            deleteReminder={deleteReminder}
            pushToast={pushToast}
            jumpTarget={jumpTarget?.listId === activeListId ? jumpTarget : null}
            onJumpHandled={() => setJumpTarget(null)}
            inline
            // Edit-lock plumbing for mobile. Same shape as desktop.
            currentUid={user?.uid}
            currentUserName={user?.displayName || user?.email || 'someone'}
            boardOwnerUid={boardOwnerUid || board?.ownerId || user?.uid}
            boardEditors={board?.editors || []}
            boardViewers={board?.viewers || []}
            onShareList={onShareList}
            onPeekShare={onPeekShare}
            onPlaceList={onPlaceList}
            isSharedWithMe={isGuestOnBoard || !!activeList?._sharedFromOwnerUid}
            onOpenReminderEditor={onOpenReminderEditor}
          />
        )}
      </div>

      {/* Walter 2026-06-09: mobile first-step prompt — the desktop
          empty-board card's equivalent. On an empty board the user sees
          the full nav; this floating card gives the same "add your first
          list" CTA so they're not left guessing. */}
      {lists.length === 0 && !listsLoading && !isGuestOnBoard && (
        <div style={{
          position: 'fixed', left: 0, right: 0, top: '23%',
          display: 'flex', justifyContent: 'center',
          padding: '0 16px', zIndex: 1500, pointerEvents: 'none',
        }}>
          <div style={{
            pointerEvents: 'auto', maxWidth: 360, width: '100%',
            background: 'var(--surface)', border: '1px solid var(--accent-dim)',
            borderRadius: 'var(--radius)', boxShadow: 'var(--shadow-modal-flat)',
            padding: '18px 20px', textAlign: 'center', fontFamily: 'var(--mono)',
          }}>
            <div style={{ fontSize: 'calc(14px * var(--ui-scale, 1))', fontWeight: 600, color: 'var(--text)', marginBottom: '6px' }}>
              This is your board. Let's add something.
            </div>
            <div style={{ fontSize: 'calc(11px * var(--ui-scale, 1))', color: 'var(--text-dim)', lineHeight: 1.55, marginBottom: '14px' }}>
              A board holds your lists and notes. Add your first list to get going.
            </div>
            <button type="button" onClick={() => handleAddList()}
              style={{ background: 'var(--accent)', color: 'var(--bg)', border: '1px solid var(--accent)', borderRadius: 'var(--radius)', fontFamily: 'var(--mono)', fontSize: 'calc(12px * var(--ui-scale, 1))', fontWeight: 600, padding: '10px 18px', cursor: 'pointer', touchAction: 'manipulation' }}>
              + add your first list
            </button>
            <div style={{ marginTop: '10px', fontSize: 'calc(10px * var(--ui-scale, 1))', color: 'var(--text-dimmer)', lineHeight: 1.5 }}>
              or tap <strong>+ new</strong> up top · your other boards are listed here too
            </div>
          </div>
        </div>
      )}

      {/* ── Modals ── */}
      {showSearch && (
        <SearchModal
          userId={user.uid}
          boards={boards}
          currentBoardId={board.id}
          onJump={handleJump}
          onClose={() => setShowSearch(false)}
        />
      )}

      <SettingsModal
        open={showSettings}
        onClose={() => setShowSettings(false)}
        theme={theme}
        onThemeChange={setTheme}
        palette={palette}
        onPaletteChange={setPalette}
        fontSize={fontSize}
        onFontSizeChange={setFontSize}
        lineHeight={lineHeight}
        onLineHeightChange={setLineHeight}
        onSignOut={onSignOut}
        // Walter 2026-05-13: account info lives as a collapsible row
        // at the top of Settings. Tap the row to expand; collapsed
        // by default so the appearance/delivery controls are right
        // there when the modal opens.
        showAccountSection
        expandAccountOnOpen={settingsExpandAccount}
        user={user}
        boards={boards}
        lists={lists}
        listsCount={lists?.length || 0}
        userPrefs={userPrefs}
        updateUserPrefs={updateUserPrefs}
        pushSub={pushSub}
        currentBoardId={board?.id}
        onLinkGoogle={onLinkGoogle}
        onLinkMagicLink={onLinkMagicLink}
      />

      <AgendaPanel
        open={showAgenda}
        onClose={() => setShowAgenda(false)}
        defaultReminderTime={userPrefs?.defaultReminderTime}
        reminders={reminders}
        lists={lists}
        updateReminder={updateReminderInAgenda || updateReminder}
        updateNotes={updateReminderNotes}
        deactivateReminder={deactivateReminder}
        deleteReminder={fullyDeleteReminder || deleteReminder}
        onJumpToReminder={(r) => {
          // Navigate to the reminder's source list (mobile uses
          // setActiveListId in the parent — easiest path here is to
          // close the agenda and rely on the user clicking through).
          setShowAgenda(false);
        }}
      />

      {/* Formatting toolbar that rides above the on-screen keyboard while a
          list textarea is focused. Mobile-only (this component isn't rendered
          on desktop). Walter 2026-07-02. */}
      <MobileFormatBar />

    </div>
  );
}

// --- Local helpers ---

function IconBtn({ children, onClick, title, accent }) {
  const [hover, setHover] = useState(false);
  return (
    <button
      onClick={onClick}
      title={title}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      style={{
        width: 32, height: 32,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        background: accent
          ? (hover ? 'var(--accent-dim)' : 'var(--accent)')
          : (hover ? 'var(--surface2)' : 'none'),
        border: accent ? '1px solid var(--accent)' : 'none',
        color: accent ? 'var(--bg)' : (hover ? 'var(--text)' : 'var(--text-dim)'),
        borderRadius: 'var(--radius)',
        cursor: 'pointer', padding: 0,
        transition: 'background 0.15s, color 0.15s',
        flexShrink: 0,
      }}
    >
      {children}
    </button>
  );
}

function MenuItem({ children, onClick, color, icon }) {
  return (
    <div
      onClick={onClick}
      style={{
        padding: '9px 14px', cursor: 'pointer',
        fontFamily: 'var(--mono)',
        fontSize: 'calc(11px * var(--ui-scale, 1))',
        color: color || 'var(--text-dim)',
        display: 'flex', alignItems: 'center', gap: '9px',
      }}
    >
      {icon !== undefined && (
        <span style={{ display: 'inline-flex', width: 14, justifyContent: 'center', flexShrink: 0, opacity: 0.85 }}>{icon}</span>
      )}
      <span style={{ flex: 1, minWidth: 0 }}>{children}</span>
    </div>
  );
}
